import React, { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, ChevronRight, Phone } from "lucide-react";
import { Header } from "../sections/Header";
import { Footer } from "../sections/Footer";
import { StickyCTA } from "../sections/StickyCTA";
import { FIRM } from "../data/mock";
import { INSIGHTS } from "../data/insights";
import {
  Seo,
  buildArticleSchema,
  buildBreadcrumbSchema,
  ORGANIZATION_SCHEMA,
  PERSON_SCHEMA,
} from "../components/Seo";

const InsightDetail = () => {
  const { slug } = useParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const post = INSIGHTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/insights" replace />;

  const bodyText = post.body
    .filter((b) => b.type === "p")
    .map((b) => b.text)
    .join(" ");

  const otherPosts = INSIGHTS.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <>
      <Seo
        title={post.title}
        description={post.excerpt}
        path={`/insights/${post.slug}`}
        type="article"
        keywords={post.keywords}
        schemas={[
          buildArticleSchema({
            title: post.title,
            description: post.excerpt,
            slug: post.slug,
            datePublished: post.datePublished,
            dateModified: post.dateModified,
            keywords: post.keywords,
            bodyText,
          }),
          ORGANIZATION_SCHEMA,
          PERSON_SCHEMA,
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Insights", path: "/insights" },
            { name: post.title, path: `/insights/${post.slug}` },
          ]),
        ]}
      />
      <main data-testid="insight-detail-page" className="bg-ivory min-h-screen">
        <Header />

        {/* Article header */}
        <article>
          <header
            data-testid="article-header"
            className="relative pt-36 lg:pt-44 pb-12 lg:pb-16 bg-ivory overflow-hidden"
          >
            <div className="absolute inset-0 pointer-events-none opacity-[0.04]">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 1px 1px, #102845 1px, transparent 0)",
                  backgroundSize: "32px 32px",
                }}
              />
            </div>

            <div className="relative max-w-4xl mx-auto px-6 lg:px-12">
              <nav
                data-testid="article-breadcrumb"
                className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-navy/60 mb-10 fade-up"
              >
                <Link to="/" className="hover:text-navy transition-colors">
                  Home
                </Link>
                <ChevronRight className="w-3 h-3" strokeWidth={2} />
                <Link to="/insights" className="hover:text-navy transition-colors">
                  Insights
                </Link>
                <ChevronRight className="w-3 h-3" strokeWidth={2} />
                <span className="text-navy font-medium truncate max-w-[200px] sm:max-w-none">
                  {post.title.split(" ").slice(0, 4).join(" ")}…
                </span>
              </nav>

              <div className="flex flex-wrap items-center gap-4 mb-6 fade-up-delay-1">
                <span className="text-[11px] uppercase tracking-[0.22em] text-gold font-medium">
                  {post.category}
                </span>
                <span className="flex items-center gap-1.5 text-xs text-navy/55">
                  <Clock className="w-3 h-3" strokeWidth={1.5} />
                  {post.readTime}
                </span>
                <span className="text-xs text-navy/55">
                  {new Date(post.datePublished).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              </div>

              <h1
                data-testid="article-title"
                className="font-serif text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05] tracking-tight fade-up-delay-2"
              >
                {post.title}
              </h1>

              <p
                data-testid="article-excerpt"
                className="mt-8 font-serif text-xl lg:text-2xl italic text-gold leading-snug max-w-3xl fade-up-delay-3"
              >
                {post.excerpt}
              </p>

              <div className="mt-12 pt-8 border-t border-[#C0974E]/20 flex items-center gap-4 fade-up-delay-4">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-navy-deep flex-shrink-0">
                  <img
                    src="https://customer-assets.emergentagent.com/job_legal-finance-3/artifacts/wh5zrplf_No-Background-4.png"
                    alt="Richard L. Bufkin"
                    className="w-full h-full object-cover object-top scale-125"
                  />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-navy/60">By</div>
                  <Link
                    to="/richard-bufkin"
                    className="font-serif text-base text-navy hover:text-gold transition-colors"
                  >
                    Richard L. Bufkin · Attorney, CPA, AICPA CFF
                  </Link>
                </div>
              </div>
            </div>
          </header>

          {/* Article body */}
          <section
            data-testid="article-body"
            className="py-12 lg:py-20 bg-ivory"
          >
            <div className="max-w-3xl mx-auto px-6 lg:px-12">
              <div className="prose prose-lg max-w-none">
                {post.body.map((block, idx) => {
                  if (block.type === "h2") {
                    return (
                      <h2
                        key={idx}
                        className="font-serif text-2xl lg:text-3xl text-navy mt-12 mb-5 leading-tight tracking-tight"
                      >
                        {block.text}
                      </h2>
                    );
                  }
                  return (
                    <p
                      key={idx}
                      className="text-base lg:text-lg text-navy/85 leading-[1.75] mb-6 font-serif"
                    >
                      {block.text}
                    </p>
                  );
                })}
              </div>

              {/* Closing CTA inline */}
              <div className="mt-16 p-8 lg:p-10 bg-cream border-l-2 border-gold">
                <div className="text-xs uppercase tracking-[0.22em] text-gold mb-3 font-medium">
                  Have a related matter?
                </div>
                <h3 className="font-serif text-2xl lg:text-3xl text-navy leading-tight">
                  Send Dick a few sentences and he'll tell you straight whether
                  he can help.
                </h3>
                <div className="mt-6 flex flex-wrap items-center gap-4">
                  <a
                    href="/#contact"
                    data-testid="article-cta"
                    className="btn-primary inline-flex items-center gap-3 px-6 py-3.5 text-xs tracking-[0.25em] uppercase font-medium group"
                  >
                    Schedule Consultation
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </a>
                  <a
                    href={`tel:${FIRM.phone.replace(/[^\d+]/g, "")}`}
                    className="inline-flex items-center gap-2 text-sm text-navy font-medium hover:text-gold transition-colors"
                  >
                    <Phone className="w-4 h-4" strokeWidth={2} />
                    {FIRM.phone}
                  </a>
                </div>
              </div>
            </div>
          </section>
        </article>

        {/* More insights */}
        <section
          data-testid="more-insights"
          className="py-16 lg:py-24 bg-cream"
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex items-center justify-between mb-10">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="gold-line" />
                  <span className="text-xs tracking-[0.3em] uppercase text-navy/70 font-medium">
                    Continue Reading
                  </span>
                </div>
                <h2 className="font-serif text-3xl lg:text-4xl text-navy">
                  More from <span className="italic text-gold">Insights</span>.
                </h2>
              </div>
              <Link
                to="/insights"
                className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-medium text-navy hover:text-gold transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
                All Insights
              </Link>
            </div>

            <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
              {otherPosts.map((p) => (
                <Link
                  key={p.slug}
                  to={`/insights/${p.slug}`}
                  data-testid={`more-insight-${p.slug}`}
                  className="group bg-white border border-[#C0974E]/20 p-8 lg:p-10 hover:border-gold/60 transition-all hover:shadow-lg hover:shadow-navy/5"
                >
                  <span className="text-[11px] uppercase tracking-[0.22em] text-gold font-medium">
                    {p.category}
                  </span>
                  <h3 className="font-serif text-xl lg:text-2xl text-navy leading-tight tracking-tight mt-3 group-hover:text-gold transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-sm text-navy/70 leading-relaxed">
                    {p.excerpt}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <Footer />
        <StickyCTA />
      </main>
    </>
  );
};

export default InsightDetail;
